<template>
  <div data-component-group="filter-forms" data-component="contract">
    <FiltersForm
      :has-filters="hasFilters"
      @filter="onFilter"
      @clear="onClearFilters"
      v-model="countries"
    >
      <v-autocomplete
        clearable
        v-model="business"
        :items="businesses"
        :loading="isLoadingBusinesses"
        prepend-inner-icon="domain"
        :return-object="true"
        item-value="id"
        item-title="name"
        label="Business"
        v-model:search="businessSearch"
      />

      <v-autocomplete
        clearable
        v-model="status"
        :items="statuses"
        prepend-inner-icon="download_done"
        :return-object="true"
        item-title="name"
        label="Status"
      />

      <base-date-picker
        :date="date"
        placeholder="Dates"
        :on-change="onChangeDate"
      />

      <v-autocomplete
        clearable
        v-model="owner"
        :items="owners"
        :loading="isLoadingManagers"
        prepend-inner-icon="person"
        label="Contract owner"
        item-title="name"
        :return-object="true"
        v-model:search="ownerSearch"
      />

      <v-autocomplete
        clearable
        v-model="domain"
        :items="domains"
        :loading="isLoadingDomains"
        prepend-inner-icon="account_circle"
        label="Domain"
        item-title="url"
        item-value="url"
        v-model:search="domainSearch"
      />

      <v-autocomplete
        clearable
        v-model="contractType"
        :items="contractTypes"
        prepend-inner-icon="label_important"
        :label="$t('contract_type')"
        item-title="name"
        return-object
      />
    </FiltersForm>
  </div>
</template>

<script>
  import { v1 as uuidv1 } from 'uuid';
  import FiltersForm from './FiltersBase';
  import { isNonEmptyArr, isNonEmptyStr, isNum, isStr, debounce } from '../../../utils';

  const businessSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const ownerSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  const domainSearchDebouncer = debounce({
    id: uuidv1(),
    timeout: 500
  });

  export default {
    components: {
      FiltersForm,
    },
    emits: ['clearFilters', 'filter'],
    data() {
      return {
        business: null,
        businessSearch: '',
        contractType: null,
        countries: [],
        domain: null,
        domains: [],
        status: null,
        owner: null,
        owners: [],
        ownerSearch: '',
        domainSearch: '',
        date: null,
        isLoadingBusinesses: false,
        isLoadingManagers: false,
        isLoadingDomains: false,
        isRefreshingBusinesses: false,
        isRefreshingOwner: false,
      };
    },
    computed: {
      hasFilters() {
        const {
          contractType = {},
          business = {},
          countries = [],
          status = {},
          owner = {},
          domain = '',
          date,
        } = this;

        return (
          isNum(contractType?.id) ||
          isNum(business?.id) ||
          isNonEmptyArr(countries) ||
          isNum(status?.id) ||
          isNum(owner?.id) ||
          isNonEmptyStr(domain) ||
          !!date
        );
      },
      contractTypes() {
        return this.$store.getters['contracts/contractTypes'];
      },
      businesses() {
        return this.$store.getters['businesses/items'];
      },
      statuses() {
        return this.$store.getters['contracts/statuses'];
      },
    },
    watch: {
      business() {
        this.isRefreshingBusinesses = true;
      },
      businessSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingBusinesses) {
            this.isRefreshingBusinesses = false;
            return false;
          }

          businessSearchDebouncer(() => this.getBusinesses(nextSearch));
        }
      },
      owner() {
        this.isRefreshingOwner = true;
      },
      ownerSearch(nextSearch) {
        if (isStr(nextSearch)) {
          if (this.isRefreshingOwner) {
            this.isRefreshingOwner = false;
            return false;
          }

          ownerSearchDebouncer(() => this.getManagers(nextSearch));
        }
      },
      domainSearch(nextSearch) {
        if (isStr(nextSearch) && nextSearch.length > 2) {
          if (this.isLoadingDomains) {
            this.isLoadingDomains = false;
            return false;
          }

          domainSearchDebouncer(() => this.getDomains(nextSearch));
        }
      },
    },
    created() {
      const { statuses } = this;

      if (statuses.length === 0) {
        this.getStatuses();
      }
    },
    methods: {
      onDomainFocus() {
        if (this.contractTypes.length === 0) {
          this.getDomains();
        }
      },
      getDomains(search = '') {
        this.isLoadingDomains = true;
        return this.$store
          .dispatch('contracts/getDomains', {search})
          .then((res) => {
            this.domains = res;
          })
          .finally(() => {
            this.isLoadingDomains = false;
          });
      },
      getStatuses() {
        return this.$store.dispatch('contracts/getStatuses');
      },
      onFilter() {
        const {
          contractType = {},
          business = {},
          countries = [],
          status = {},
          owner = {},
          domain = '',
          date,
        } = this;

        this.$emit('filter', {
          type: contractType?.id,
          business: business?.id,
          countries,
          status: status?.id,
          owner: owner?.id,
          domain,
          expire_before: date,
        });
      },
      onClearFilters() {
        this.countries = [];
        this.domainSearch = '';
        this.date = null;

        this.domain = null;
        this.owner = null;
        this.status = null;
        this.contractType = null;
        this.business = null;

        this.$emit('clearFilters');
      },
      onChangeDate(date) {
        this.date = date;
      },
      getBusinesses(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingBusinesses = true;
        return this.$store
          .dispatch('businesses/getItems', query)
          .then((res) => {
            this.isLoadingBusinesses = false;
            return res;
          })
          .catch((err) => {
            this.isLoadingBusinesses = false;
            return err;
          });
      },
      getManagers(search = '') {
        const query = {
          per_page: -1,
          search,
        };

        this.isLoadingManagers = true;
        return this.$store
          .dispatch('users/getManagers', query)
          .then((items) => {
            this.owners = items;
            this.isLoadingManagers = false;
            return items;
          })
          .catch((err) => {
            this.isLoadingManagers = false;
            return err;
          });
      },
    },
  };
</script>
